import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TicketPercent, Plus, Ban, Loader2, X } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

type VoucherStatus = 'issued' | 'redeemed' | 'expired' | 'cancelled';

interface Voucher {
  id: number;
  phone: string;
  amount: number;
  status: VoucherStatus;
  sourceEvent: string;
  sourceOrderId: number | null;
  redeemedOrderId: number | null;
  expiresAt: number;
  createdAt: number;
  redeemedAt: number | null;
  sourceOrderNum: string | null;
  redeemedOrderNum: string | null;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'issued', label: '已發放' },
  { value: 'redeemed', label: '已使用' },
  { value: 'expired', label: '已過期' },
  { value: 'cancelled', label: '已作廢' },
  { value: 'all', label: '全部' },
];

const STATUS_BADGE: Record<VoucherStatus, { label: string; className: string }> = {
  issued: { label: '已發放', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  redeemed: { label: '已使用', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  expired: { label: '已過期', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
  cancelled: { label: '已作廢', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

const THREE_DAYS_SECONDS = 3 * 24 * 60 * 60;

const AdminVouchers: React.FC = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('issued');
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  // 使用門檻設定
  const [redeemThreshold, setRedeemThreshold] = useState<number>(100);
  const [editingThreshold, setEditingThreshold] = useState<number>(100);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [thresholdLoading, setThresholdLoading] = useState(true);

  // 手動發放 Modal
  const [showModal, setShowModal] = useState(false);
  const [modalPhone, setModalPhone] = useState('');
  const [modalError, setModalError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  }, [navigate]);

  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const res = await apiFetch(`/api/public/admin/vouchers${query}`);
      if (res.ok) {
        setVouchers(await res.json());
      } else if (res.status === 401) {
        handleUnauthorized();
      } else {
        setError('獲取回贈券列表失敗');
      }
    } catch {
      setError('獲取回贈券列表失敗');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, handleUnauthorized]);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin');
      return;
    }
    fetchVouchers();
    fetchSettings();
  }, [fetchVouchers, navigate]);

  const fetchSettings = useCallback(async () => {
    try {
      setThresholdLoading(true);
      const res = await apiFetch('/api/public/admin/voucher-settings');
      if (res.ok) {
        const data = await res.json();
        const t = Number(data.redeemThreshold);
        if (Number.isFinite(t) && t >= 0) {
          setRedeemThreshold(t);
          setEditingThreshold(t);
        }
      }
    } catch {
      // ignore, fallback to default 100
    } finally {
      setThresholdLoading(false);
    }
  }, []);

  const handleSaveThreshold = async () => {
    if (!Number.isFinite(editingThreshold) || editingThreshold < 0) {
      setError('使用門檻必須為非負數字');
      return;
    }
    setSavingThreshold(true);
    setError('');
    try {
      const res = await apiFetch('/api/public/admin/voucher-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ redeemThreshold: editingThreshold }),
      });
      if (res.ok) {
        setRedeemThreshold(editingThreshold);
        showMessage(`回贈券使用門檻已更新為 HK$${editingThreshold}`);
      } else if (res.status === 401) {
        handleUnauthorized();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '更新失敗');
      }
    } catch {
      setError('更新失敗');
    } finally {
      setSavingThreshold(false);
    }
  };

  const showMessage = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 4000);
  };

  const handleCancel = async (voucher: Voucher) => {
    if (!confirm(`確定要作廢此回贈券嗎？（客戶電話：${voucher.phone}，面額 HK$${voucher.amount}）`)) return;
    try {
      setCancellingId(voucher.id);
      const res = await apiFetch(`/api/public/admin/vouchers/${voucher.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      if (res.ok) {
        showMessage('回贈券已作廢');
        fetchVouchers();
      } else if (res.status === 401) {
        handleUnauthorized();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '作廢失敗');
      }
    } catch {
      setError('作廢失敗');
    } finally {
      setCancellingId(null);
    }
  };

  const openModal = () => {
    setModalPhone('');
    setModalError('');
    setShowModal(true);
  };

  const handleIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    const phone = modalPhone.trim();
    if (!phone) {
      setModalError('請輸入客戶電話');
      return;
    }
    try {
      setSubmitting(true);
      setModalError('');
      const res = await apiFetch('/api/public/admin/vouchers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && !data.error) {
        setShowModal(false);
        showMessage(`已向 ${phone} 發放 HK$30 回贈券（15 天有效）`);
        fetchVouchers();
      } else if (res.status === 401) {
        handleUnauthorized();
      } else {
        setModalError(data.error || '發放失敗，請稍後再試');
      }
    } catch {
      setModalError('發放失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (ts: number) => new Date(ts * 1000).toLocaleString('zh-HK');
  const formatDate = (ts: number) => new Date(ts * 1000).toLocaleDateString('zh-HK');

  const isExpiringSoon = (v: Voucher) =>
    v.status === 'issued' && v.expiresAt - Math.floor(Date.now() / 1000) < THREE_DAYS_SECONDS;

  const renderSource = (v: Voucher) => {
    const main =
      v.sourceEvent === 'order_paid'
        ? `訂單賺取${v.sourceOrderNum ? ` #${v.sourceOrderNum}` : ''}`
        : v.sourceEvent === 'manual'
          ? '手動發放'
          : v.sourceEvent;
    return (
      <div>
        <span className="text-gray-700 dark:text-gray-300">{main}</span>
        {v.redeemedOrderNum && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">已用於 #{v.redeemedOrderNum}</div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout currentPage="vouchers" onLogout={handleUnauthorized}>
      <div className="p-4 md:p-8">
        {/* 優惠總覽 */}
        <div className="mb-6 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-100 dark:border-orange-800 rounded-2xl p-5">
          <h2 className="text-lg font-bold text-orange-800 dark:text-orange-200 mb-3">優惠總覽</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-orange-100 dark:border-orange-800">
              <div className="font-bold text-stone-800 dark:text-white mb-1">🎁 復購回贈券</div>
              <p className="text-sm text-stone-600 dark:text-gray-300">
                實付滿 HK$150 自動送 HK$30 回贈券，15 天內有效，下次落單滿 HK${redeemThreshold} 自動扣減。
              </p>
              <div className="mt-3 flex items-center gap-2">
                <label className="text-xs text-stone-600 dark:text-gray-400 whitespace-nowrap">使用門檻 HK$</label>
                <input
                  type="number"
                  min={0}
                  value={thresholdLoading ? '' : editingThreshold}
                  disabled={thresholdLoading || savingThreshold}
                  onChange={e => setEditingThreshold(Number(e.target.value))}
                  className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50"
                />
                <button
                  onClick={handleSaveThreshold}
                  disabled={savingThreshold || thresholdLoading || editingThreshold === redeemThreshold}
                  className="px-2 py-1 text-xs font-medium bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 transition"
                >
                  {savingThreshold ? '儲存中...' : '更新'}
                </button>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-orange-100 dark:border-orange-800">
              <div className="font-bold text-stone-800 dark:text-white mb-1">🎉 首次網購優惠</div>
              <p className="text-sm text-stone-600 dark:text-gray-300">新客戶首次網購滿 HK$100 即減 HK$20，每電話限一次。</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-orange-100 dark:border-orange-800">
              <div className="font-bold text-stone-800 dark:text-white mb-1">🥚 雞蛋活動掃碼券</div>
              <p className="text-sm text-stone-600 dark:text-gray-300">線下掃碼加 WhatsApp，即領 HK$30 回贈券（10 天有效，滿 HK${redeemThreshold} 自動扣減）。</p>
              <button
                type="button"
                onClick={() => window.open('/egg-voucher', '_blank')}
                className="mt-2 text-sm font-bold text-orange-600 hover:underline"
              >
                查看活動頁面 →
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <TicketPercent className="text-orange-600" />
              復購回贈券
            </h1>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={openModal}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
            >
              <Plus size={20} /> 手動發放
            </button>
          </div>
        </div>

        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Loader2 className="animate-spin" size={18} /> 載入中...
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">ID</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">客戶電話</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">面額</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">來源</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">發放時間</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">到期時間</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">狀態</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {vouchers.map(v => {
                    const badge = STATUS_BADGE[v.status] || STATUS_BADGE.expired;
                    return (
                      <tr key={v.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{v.id}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{v.phone}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">HK${v.amount}</td>
                        <td className="px-4 py-3">{renderSource(v)}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {formatDateTime(v.createdAt)}
                        </td>
                        <td
                          className={`px-4 py-3 whitespace-nowrap ${
                            isExpiringSoon(v)
                              ? 'text-red-600 dark:text-red-400 font-medium'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {formatDate(v.expiresAt)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {v.status === 'issued' && (
                            <button
                              onClick={() => handleCancel(v)}
                              disabled={cancellingId === v.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {cancellingId === v.id ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                              作廢
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {vouchers.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        暫時沒有符合條件的回贈券。
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 手動發放 Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">手動發放回贈券</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleIssue} className="p-6 space-y-5">
                {modalError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    {modalError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    客戶電話 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={modalPhone}
                    onChange={e => setModalPhone(e.target.value)}
                    placeholder="852XXXXXXXX"
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    系統會向此電話發放一張 HK$30 回贈券，15 天有效。
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                    確認發放
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-bold rounded-lg transition"
                  >
                    <X size={18} /> 取消
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminVouchers;
